import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DialPad from "./DialPad";
import ActiveCallBar from "./ActiveCallBar";
import CallHistoryPanel from "./CallHistoryPanel";
import AudioDeviceSelector from "./AudioDeviceSelector";
import PhoneStatusBar from "./PhoneStatusBar";
import { BrowserPhoneState } from "./telephonyTypes";
import { useBrowserPhoneContext } from "./BrowserPhoneProvider";
import useCallCenterT from "../../../hooks/useCallCenterT";
import { useCallCenterCalls } from "../../../hooks/useCallCenterCalls";
import { getBrowserPhonePanelError, isBrowserPhoneUnavailable } from "./telephonyErrorMessages";
import { placeCall, runTelephonyAction } from "./placeCall";
import { getBrowserPhoneDisableMessage } from "./browserPhoneMessages";

const BrowserPhonePanel = () => {
  const t = useCallCenterT();
  const navigate = useNavigate();
  const { store, actions } = useBrowserPhoneContext();
  const { createManual, loading: callLoading } = useCallCenterCalls();
  const browserPhoneEnabled = Boolean(store.config?.enableBrowserPhone);
  const providerName = store.config?.providerName;

  const isIncoming =
    browserPhoneEnabled &&
    (store.state === BrowserPhoneState.INCOMING_CALL ||
      store.state === BrowserPhoneState.INCOMING_CONNECTING);
  const isCallActive =
    browserPhoneEnabled &&
    (store.state === BrowserPhoneState.OUTGOING_CALL ||
      store.state === BrowserPhoneState.ACTIVE_CALL ||
      store.state === BrowserPhoneState.HOLD);
  const registrationFailed = browserPhoneEnabled && isBrowserPhoneUnavailable(store);
  const isConnecting = browserPhoneEnabled && store.state === BrowserPhoneState.CONNECTING;
  const phoneReady = !browserPhoneEnabled || store.state === BrowserPhoneState.REGISTERED;
  const showDialPad = !isCallActive && !isIncoming;
  const translatedError = getBrowserPhonePanelError(store, t, { providerName });
  const statusState = registrationFailed
    ? BrowserPhoneState.REGISTRATION_FAILED
    : browserPhoneEnabled
      ? store.state
      : BrowserPhoneState.REGISTRATION_FAILED;
  const extensionLabel =
    store.config?.extension ||
    store.config?.sipExtension ||
    store.config?.agentExtension ||
    "EEMAD";

  const handleDial = useCallback(
    (phoneNumber) =>
      placeCall({
        browserPhoneEnabled,
        providerName,
        actions,
        createManual,
        phoneNumber,
        toast,
        t,
      }),
    [actions, browserPhoneEnabled, createManual, providerName, t]
  );

  const handleHangup = useCallback(
    () => runTelephonyAction({ action: actions.endCall, toast, t, providerName }),
    [actions, providerName, t]
  );

  const handleHold = useCallback(
    (hold) =>
      runTelephonyAction({ action: () => actions.setHold(hold), toast, t, providerName }),
    [actions, providerName, t]
  );

  return (
    <div className="mx-auto w-full space-y-4">
      <PhoneStatusBar
        state={statusState}
        error={translatedError}
        t={t}
        showDisconnect={browserPhoneEnabled}
        onDisconnect={actions.disconnect}
      />

      {!browserPhoneEnabled && (
        <p className="px-2 text-center text-sm leading-6 text-slate-400">
          {getBrowserPhoneDisableMessage(store.config, t)}
        </p>
      )}

      {isCallActive && (
        <ActiveCallBar
          activeCall={store.activeCall}
          hold={store.hold}
          muted={store.muted}
          connecting={store.state === BrowserPhoneState.OUTGOING_CALL}
          onMuteChange={actions.setMute}
          onHoldChange={handleHold}
          onHangup={handleHangup}
          onContacts={() => navigate("/call-center/tickets")}
          onNote={() => navigate("/call-center/tickets")}
          extensionLabel={extensionLabel}
          variant="panel"
        />
      )}

      {showDialPad && (
        <div className="space-y-2">
          <DialPad
            onDial={handleDial}
            disabled={isConnecting || callLoading || registrationFailed || !phoneReady}
            calling={
              !registrationFailed &&
              (isConnecting || callLoading || (browserPhoneEnabled && !phoneReady))
            }
            callLabel={t("ccDialerCall")}
            callingLabel={
              registrationFailed
                ? t("ccPhoneStatusUnavailable")
                : browserPhoneEnabled
                  ? phoneReady
                    ? t("ccDialerCall")
                    : t("ccGlobalDialerConnecting")
                  : t("ccInitiatingCall")
            }
            placeholder={t("ccDialerEnterNumber")}
          />
          {browserPhoneEnabled && (
            <button
              type="button"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-center text-xs font-semibold text-slate-700 hover:bg-slate-100"
              onClick={() =>
                runTelephonyAction({ action: actions.resetCall, toast, t, providerName })
              }
            >
              {t("ccGlobalDialerResetPhone")}
            </button>
          )}
        </div>
      )}

      <details className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm">
        <summary className="cursor-pointer list-none font-medium text-slate-700">
          <span className="inline-flex items-center gap-2">
            <span className="text-slate-400">{">"}</span>
            {t("ccPhoneAdvancedSettings")}
          </span>
        </summary>
        <div className="mt-3 space-y-3">
          {browserPhoneEnabled && (
            <AudioDeviceSelector disabled={store.state === BrowserPhoneState.DISCONNECTED} />
          )}
          <CallHistoryPanel history={store.history} />
        </div>
      </details>
    </div>
  );
};

export default BrowserPhonePanel;
